import useSWRMutation from 'swr/mutation';
import { AsmPrintSettings, BadgePrintingData, SaveAsmBadgePrint, SavePrintJob } from '../api/dtos';
import { useSaveAsmBadge } from './useSaveAsmBadge';

export interface PrintBadgesResult {
    count: number;
    failedJobs: number;
    errors?: PrintBadgesError[];
}

export interface PrintBadgesError {
    message: string;
    registrationId?: number;
}

export interface PrintBadgesArgs {
    badges: BadgePrintingData[];
    printSettings: AsmPrintSettings;
    barTenderUrl: string;
}

async function printBadgesFetcher(
    _key: string,
    { arg }: { arg: PrintBadgesArgs }
): Promise<PrintBadgesResult> {
    const { badges, printSettings, barTenderUrl } = arg;

    // Validation
    if (!printSettings) {
        throw new Error('Printer is required');
    }

    if (!badges || badges.length === 0) {
        throw new Error('No badges to print');
    }

    if (!printSettings.printer) {
        throw new Error('Printer is required');
    }

    if (!barTenderUrl) {
        throw new Error('Printing API URL is not configured');
    }

    let count = 0;
    let failedJobs = 0;
    const errorList: PrintBadgesError[] = [];
    

    for (const badge of badges) {
        try {
            // Create data sources dictionary
            const dataSources: { [key: string]: string } = {
                FULL_NAME: badge.fullName || '',
                ADDRESS: badge.adress || '',
                TITLE: badge.title || '',
                FIRST_NAME: badge.firstName || '',
                LAST_NAME: badge.lastName || '',
                INSTITUTION: badge.institution || '',
                SPECIALITY: badge.speciality || '',
                QR_CODE: badge.qrCode || '',
                EMAIL: badge.email || '',
                YEARS_ATTENDED: (badge.yearsAttended || 0).toString(),
            };

            // Create BarTender payload
            const payloadBadgeFront = {
                PrintBTWAction: {
                    Document: printSettings.labelFilePath,
                    Printer: printSettings.printer,
                    PrintToFileFolder: printSettings.printToFileFolder,
                    // Required when printing to PDF — BarTender needs the filename to write the output file
                    ...(printSettings.printer === 'PDF' && {
                        PrintToFileFileName: `${badge.lastName || 'badge'}.pdf`,
                    }),
                    SaveAfterPrint: true,
                    NamedDataSources: dataSources,
                },
            };

            // Send to BarTender API`
            const response = await fetch(barTenderUrl, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payloadBadgeFront),
            });

            if (!response.ok) {
                failedJobs++;
                errorList.push({
                    message: `Failed to print badge: ${response.statusText}`,
                    registrationId: badge.id,
                });
            } else {
                count++;
            }
        } catch (error: any) {
            failedJobs++;
            errorList.push({
                message: error?.message || 'Unknown error while printing badge',
                registrationId: badge.id,
            });
        }
    }

    return {
        count,
        failedJobs,
        errors: errorList,
    };
}

export const usePrintAsmBadges = () => {
    const { trigger, data, error, isMutating } = useSWRMutation(
        'PrintASMBadges',
        printBadgesFetcher
    );

    const { saveAsmBadge } = useSaveAsmBadge();

    const printBadgesAndSave = async (args: PrintBadgesArgs) => {
        const result = await trigger(args);

        if (result) {
            // convert  badges to print jobs for saving
            const printJobs = args.badges.map(badge => new SavePrintJob({
                registrationId: badge.id,
                printed: !result.errors?.some(e => e.registrationId === badge.id),
                failed: result.errors?.some(e => e.registrationId === badge.id) ?? false
            }));

            
            try {
                await saveAsmBadge(new SaveAsmBadgePrint({
                    printJobs: printJobs
                }));
            } catch (saveError) {
                console.error('Failed to save badge print status:', saveError);
            }
        }

        return result;
    };

    return {
        printBadges: printBadgesAndSave,
        data,
        error,
        pending: isMutating,
        response: data,
        errors: data?.errors || [],
    };
};

export default usePrintAsmBadges;

